import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../context/AuthContext';
import {
  MapPin, RefreshCw, CheckCircle, Clock, ExternalLink, Save,
  ClipboardList, ArrowRight, Copy, Check,
} from 'lucide-react';

const STATUS_BADGE = {
  verified: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300',
  pending_verification: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  not_claimed: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

const BING_PLACES_URL = 'https://www.bingplaces.com';

const CHECKLIST = [
  'Sign in with your Microsoft account (or create one free)',
  'Click "Add your business" and enter your business name',
  'Fill in your address, phone, and website using the details on this page',
  'Choose your business category',
  'Submit — Bing will mail a PIN to your address to verify',
  'Come back here and click "I\'ve submitted my listing"',
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-2 text-slate-400 hover:text-teal-600 transition-colors"
      title="Copy"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-teal-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function InfoRow({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-slate-100"
        />
        {value && <CopyButton text={value} />}
      </div>
    </div>
  );
}

export default function BingPlacesPage() {
  const [activeTab, setActiveTab] = useState('status');
  const [listingStatus, setListingStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState('');

  // Guided claim wizard
  const [claimStep, setClaimStep] = useState(1); // 1 = review info, 2 = go to bing, 3 = confirm
  const [businessName, setBusinessName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [infoLoaded, setInfoLoaded] = useState(false);

  // Update form
  const [updName, setUpdName] = useState('');
  const [updAddress, setUpdAddress] = useState('');
  const [updPhone, setUpdPhone] = useState('');
  const [updWebsite, setUpdWebsite] = useState('');
  const [updHours, setUpdHours] = useState('');
  const [updDescription, setUpdDescription] = useState('');

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/bing-places/status');
      setListingStatus(data);
    } catch {
      // not claimed yet
    }
  };

  const loadBusinessInfo = async () => {
    if (infoLoaded) return;
    try {
      const { data } = await api.get('/bing-places/business-info');
      if (data.business_name) setBusinessName(data.business_name);
      if (data.address) setAddress(data.address);
      if (data.phone) setPhone(data.phone);
      if (data.website) setWebsite(data.website);
      setInfoLoaded(true);
    } catch {
      setInfoLoaded(true);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  // Pre-load business info when on status tab and not claimed
  const statusKey = listingStatus?.status || 'not_claimed';
  useEffect(() => {
    if (activeTab === 'status' && statusKey === 'not_claimed') {
      loadBusinessInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusKey]);

  const handleOpenBing = () => {
    window.open(BING_PLACES_URL, '_blank', 'noopener,noreferrer');
    setClaimStep(3);
  };

  const handleConfirmClaim = async () => {
    setLoading(true);
    setActionStatus('');
    try {
      // Save the business info we used so it persists
      const payload = { status: 'pending_verification' };
      if (businessName) payload.business_name = businessName;
      if (address) payload.address = address;
      if (phone) payload.phone = phone;
      if (website) payload.website = website;
      await api.patch('/bing-places/update', payload);
      await api.post('/bing-places/claim');
      await loadStatus();
      setClaimStep(1);
    } catch (err) {
      setActionStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setActionStatus('Updating listing...');
    try {
      const payload = {};
      if (updName) payload.business_name = updName;
      if (updAddress) payload.address = updAddress;
      if (updPhone) payload.phone = updPhone;
      if (updWebsite) payload.website = updWebsite;
      if (updHours) payload.hours = updHours;
      if (updDescription) payload.description = updDescription;

      const { data } = await api.patch('/bing-places/update', payload);
      setActionStatus(data.message || 'Listing updated successfully.');
      await loadStatus();
    } catch (err) {
      setActionStatus(`Error: ${errDetail(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'status', label: 'Status' },
    { id: 'update', label: 'Update Listing' },
  ];

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
          <MapPin className="w-5 h-5 text-teal-600 dark:text-teal-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Bing Places</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-sm">
            Claim and manage your Bing Places for Business listing to improve local visibility on Bing.
          </p>
        </div>
      </div>

      {/* Status card */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 shadow-sm flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {statusKey === 'verified' ? (
            <CheckCircle className="w-6 h-6 text-green-500" />
          ) : (
            <Clock className="w-6 h-6 text-amber-500" />
          )}
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">Listing Status</p>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_BADGE[statusKey] || STATUS_BADGE.not_claimed}`}>
              {statusKey.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {listingStatus?.live_url && (
            <a
              href={listingStatus.live_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700 font-medium"
            >
              View on Bing <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <button
            type="button"
            onClick={loadStatus}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`pb-2 px-1 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-b-2 border-teal-600 text-teal-600'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm">

        {/* STATUS TAB */}
        {activeTab === 'status' && (
          <div className="space-y-6">

            {/* Already verified */}
            {statusKey === 'verified' && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300">
                <CheckCircle className="w-5 h-5 shrink-0" />
                <p className="text-sm font-medium">Your listing is verified and live on Bing Places.</p>
              </div>
            )}

            {/* Pending verification */}
            {statusKey === 'pending_verification' && (
              <div className="space-y-4 max-w-lg">
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 text-sm">
                  <p className="font-medium mb-1">Waiting for postcard verification</p>
                  <p className="text-amber-700 dark:text-amber-400">Bing will mail a PIN code to your business address within 1–2 weeks. Enter it on Bing Places to complete verification.</p>
                </div>
                <a
                  href={BING_PLACES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700"
                >
                  Enter PIN on Bing Places <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {/* Not claimed — 3-step guided wizard */}
            {statusKey === 'not_claimed' && (
              <div className="space-y-6">
                {/* Step indicators */}
                <div className="flex items-center gap-2">
                  {[1, 2, 3].map((n) => (
                    <div key={n} className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        claimStep === n
                          ? 'bg-teal-600 text-white'
                          : claimStep > n
                          ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                          : 'bg-slate-100 text-slate-400 dark:bg-slate-800'
                      }`}>
                        {claimStep > n ? <Check className="w-3.5 h-3.5" /> : n}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${claimStep === n ? 'text-teal-600' : 'text-slate-400'}`}>
                        {n === 1 ? 'Review info' : n === 2 ? 'Go to Bing' : 'Confirm'}
                      </span>
                      {n < 3 && <ArrowRight className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />}
                    </div>
                  ))}
                </div>

                {/* Step 1: Review business info */}
                {claimStep === 1 && (
                  <div className="space-y-4 max-w-lg">
                    <div>
                      <h2 className="text-base font-semibold text-slate-900 dark:text-white">Review your business info</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                        You'll copy this to Bing Places. Edit anything that's wrong before continuing.
                      </p>
                    </div>
                    <InfoRow label="Business Name" value={businessName} onChange={setBusinessName} />
                    <InfoRow label="Address" value={address} onChange={setAddress} />
                    <InfoRow label="Phone" value={phone} onChange={setPhone} />
                    <InfoRow label="Website" value={website} onChange={setWebsite} />
                    <button
                      type="button"
                      onClick={() => setClaimStep(2)}
                      className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2 rounded-md text-sm hover:bg-teal-700"
                    >
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Step 2: Go to Bing */}
                {claimStep === 2 && (
                  <div className="space-y-5 max-w-lg">
                    <div>
                      <h2 className="text-base font-semibold text-slate-900 dark:text-white">Submit your listing on Bing Places</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                        Click the button below — it opens Bing Places in a new tab. Follow these steps:
                      </p>
                    </div>
                    <ol className="space-y-2.5">
                      {CHECKLIST.map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                          <span className="w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-xs flex items-center justify-center shrink-0 font-medium mt-0.5">
                            {i + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleOpenBing}
                        className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2 rounded-md text-sm hover:bg-teal-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open Bing Places
                      </button>
                      <button
                        type="button"
                        onClick={() => setClaimStep(1)}
                        className="text-sm text-slate-500 hover:text-slate-700"
                      >
                        Back
                      </button>
                    </div>
                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                      <p className="font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Your details to copy</p>
                      {businessName && <p><span className="font-medium">Name:</span> {businessName} <CopyButton text={businessName} /></p>}
                      {address && <p><span className="font-medium">Address:</span> {address} <CopyButton text={address} /></p>}
                      {phone && <p><span className="font-medium">Phone:</span> {phone} <CopyButton text={phone} /></p>}
                      {website && <p><span className="font-medium">Website:</span> {website} <CopyButton text={website} /></p>}
                    </div>
                  </div>
                )}

                {/* Step 3: Confirm */}
                {claimStep === 3 && (
                  <div className="space-y-4 max-w-lg">
                    <div>
                      <h2 className="text-base font-semibold text-slate-900 dark:text-white">Did you submit your listing?</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                        Once you've submitted on Bing Places, click below to track the status here.
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 text-sm text-slate-600 dark:text-slate-400">
                      Bing will mail a PIN postcard to your business address. When it arrives, go back to Bing Places and enter the PIN to complete verification.
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleConfirmClaim}
                        disabled={loading}
                        className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2 rounded-md text-sm hover:bg-teal-700 disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {loading ? 'Saving...' : "I've submitted my listing"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setClaimStep(2)}
                        className="text-sm text-slate-500 hover:text-slate-700"
                      >
                        Not yet — go back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* UPDATE TAB */}
        {activeTab === 'update' && (
          <form onSubmit={handleUpdate} className="space-y-5 max-w-2xl">
            <p className="text-sm text-slate-500 dark:text-slate-400">Fill in only the fields you want to update.</p>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Business Name</label>
                <input
                  type="text"
                  value={updName}
                  onChange={(e) => setUpdName(e.target.value)}
                  placeholder="Acme Corp"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone</label>
                <input
                  type="text"
                  value={updPhone}
                  onChange={(e) => setUpdPhone(e.target.value)}
                  placeholder="+1 555-000-0000"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Address</label>
              <input
                type="text"
                value={updAddress}
                onChange={(e) => setUpdAddress(e.target.value)}
                placeholder="123 Main St, City, State, ZIP"
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Website</label>
                <input
                  type="url"
                  value={updWebsite}
                  onChange={(e) => setUpdWebsite(e.target.value)}
                  placeholder="https://yoursite.com"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Business Hours</label>
                <input
                  type="text"
                  value={updHours}
                  onChange={(e) => setUpdHours(e.target.value)}
                  placeholder="Mon–Fri 9am–6pm"
                  className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Business Description</label>
              <textarea
                value={updDescription}
                onChange={(e) => setUpdDescription(e.target.value)}
                placeholder="Short description of your business..."
                rows={3}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2 rounded-md text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        )}

        {actionStatus && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            actionStatus.startsWith('Error')
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200'
          }`}>
            {actionStatus}
          </div>
        )}
      </div>
    </motion.div>
  );
}
